import { LoadingContainer } from 'peach/components'
import { getName } from 'peach/helpers'
import { useHistory, useParams } from 'peach/router'

import {
  ConfirmDialog,
  Clickable,
  DynamicForm,
  Page,
  PageHeader,
} from 'core/components'
import {
  useDeleteWorkflowType,
  useGetWorkflowStepTypes,
  useGetWorkflowTypeById,
  useUpdateWorkflowType,
} from 'core/types'
import {
  workflowTypeSchema,
  type StaticFieldSchema,
} from 'core/utils/generated/formSchemas'

const WorkflowType = () => {
  const history = useHistory()
  const { companyId, workflowTypeId } = useParams<{
    companyId?: string
    workflowTypeId?: string
  }>()

  const {
    data: workflowType,
    isLoading,
    refetch,
  } = useGetWorkflowTypeById({
    companyId,
    workflowTypeId,
  })

  const { mutateAsync: updateWorkflowType } = useUpdateWorkflowType({
    companyId,
    workflowTypeId,
  })

  const { mutateAsync: deleteWorkflowType } = useDeleteWorkflowType({
    companyId,
    workflowTypeId,
  })

  // Build MultiSelect options for steps from existing workflow step types
  const { data: stepTypes } = useGetWorkflowStepTypes({ companyId })
  const stepOptions: Array<{
    label: string
    value: string
    sublabel?: string
  }> = Array.isArray(stepTypes)
    ? (stepTypes as Array<any>)
        .map((s) => {
          const id = s?.id as string | undefined
          if (!id) return null
          const desc = (s?.descriptionShort ||
            s?.descriptionLong ||
            '') as string
          const truncated = desc
            ? desc.length > 70
              ? desc.slice(0, 70) + '…'
              : desc
            : undefined
          return truncated
            ? { label: id, value: id, sublabel: truncated }
            : { label: id, value: id }
        })
        .filter(
          (v): v is { label: string; value: string; sublabel?: string } =>
            v !== null,
        )
    : []

  const enhancedSchema: Array<StaticFieldSchema> = (
    workflowTypeSchema as Array<StaticFieldSchema>
  ).map((f) => (f.key === 'steps' ? { ...f, options: stepOptions } : f))

  const title = getName(workflowType)

  return (
    <LoadingContainer isLoading={isLoading}>
      <Page variant='full'>
        <PageHeader title={title || 'Workflow Type'} />
        <DynamicForm
          data={workflowType || {}}
          fieldEditMode='toggle'
          fieldOverrides={{ steps: 'MultiSelect' }}
          onSubmit={async (payload: any) => {
            await updateWorkflowType({
              ...payload,
              description: payload.description || '',
            })
            await refetch()
          }}
          otherActions={[
            <ConfirmDialog
              key='delete-action'
              onConfirm={async () => {
                await deleteWorkflowType()
                history.push(`/companies/${companyId}/workflow-types`)
              }}
              title='Delete Workflow Type'
            >
              <Clickable variant='outline'>Delete</Clickable>
            </ConfirmDialog>,
          ]}
          schema={enhancedSchema}
          validationRules={{
            name: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'name is required',
            ],
            refId: [
              (v: unknown) =>
                (typeof v === 'string' && v.trim().length > 0) ||
                'refId is required',
            ],
          }}
        />
      </Page>
    </LoadingContainer>
  )
}

export default WorkflowType
